import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Grid, makeStyles } from '@material-ui/core'
import { dataMarts } from '../../../../../../api/coreData'
import SydButton from '../../../../../commonDesign/Button'
import SydInput from '../../../../../commonDesign/SydInput'
import SydSelect from '../../../../../commonDesign/SydSelect'
import SydSwitch from '../../../../../commonDesign/SydSwitch'
import FlexRow from '../../../../../molecules/FlexRow'
import ColumnsEditor from './ColumnsEditor'
import IndexesEditor from './IndexesEditor'
import JsonEditor from './JsonEditor'

const MIN_W = '0'

const METADATA_VARS = ['batchId', 'taskId', 'timeSeriesId']
const ENTITY_VARS = ['clientIds', 'accountIds', 'assetIds', 'levelIds', 'tags']
const SKIP_TAKE_VARS = ['skip', 'take']

const TYPE_SPECIFIC_VARS = {
  fact: [],
  timeseries: ['periodStartDate', 'periodEndDate', 'startDate', 'endDate', 'asOfDate', 'period', 'periodLabel'],
  snapshot: ['asOfDate', 'snapshotDate']
}

function getAutoProvidedVars (workflowType, runBy) {
  const typeVars = TYPE_SPECIFIC_VARS[workflowType] || []
  let exposureVars
  if (runBy === 'system-query' || runBy === 'pipeline') {
    exposureVars = SKIP_TAKE_VARS
  } else if (runBy === 'none') {
    exposureVars = []
  } else {
    exposureVars = ENTITY_VARS
  }
  return new Set([...METADATA_VARS, ...exposureVars, ...typeVars])
}

const useStyles = makeStyles((theme) => ({
  section: {
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-md)',
    padding: theme.layout.padding.p10,
    marginBottom: theme.layout.gap.g5
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: theme.layout.gap.g5
  },
  label: {
    fontWeight: 500,
    marginBottom: '4px',
    display: 'block'
  },
  typeButton: {
    minWidth: '120px'
  },
  infoNote: {
    padding: theme.layout.padding.p10,
    background: 'var(--color-surface-secondary)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '0.875rem'
  }
}))

function ConfigurationTab ({
  form, isNew, isSaving,
  pipelineList, pipelineDetail,
  setField, setConfig, setTableConfig, setBehavior, setWorkflowType,
  deriveColumnsFromPipeline, onSave,
  WORKFLOW_TYPES, RUN_BY_OPTIONS, PERIOD_OPTIONS, DATA_TYPES
}) {
  const classes = useStyles()

  const workflowType = form.configuration.workflowType
  const runBy = form.configuration.runBy

  const { data: systemQueryData } = dataMarts.useListSystemQueries({ enabled: runBy === 'system-query' })
  const systemQueryList = systemQueryData?.systemQueries || []

  const pipelineVariables = useMemo(() => {
    const vars = pipelineDetail?.pipeline?.variables
    if (!vars) return null
    const filterEntries = Object.entries(vars.filters || {})
    const dateEntries = Object.entries(vars.dates || {})
    if (filterEntries.length === 0 && dateEntries.length === 0) return null
    const autoVars = getAutoProvidedVars(workflowType, runBy)
    const autoProvided = filterEntries.filter(([k]) => autoVars.has(k))
    const userConfigurable = filterEntries.filter(([k]) => !autoVars.has(k))
    return { autoProvided, userConfigurable, dateEntries }
  }, [pipelineDetail, workflowType, runBy])

  const defaultParams = useMemo(() => form.configuration.defaultParameters || {}, [form.configuration.defaultParameters])

  const handleDefaultParamChange = useCallback((key, value) => {
    setConfig('defaultParameters', { ...defaultParams, [key]: value })
  }, [defaultParams, setConfig])

  const handleExposureFilterChange = useCallback((parsed) => {
    setConfig('exposureFilter', parsed)
  }, [setConfig])

  return (
    <Grid container spacing={2}>
      {/* Basic Info */}
      <Grid item xs={12}>
        <div className={classes.section}>
          <div className={classes.sectionTitle}>Basic Info</div>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <label className={classes.label}>Code</label>
              <FlexRow style={{ gap: '8px', alignItems: 'center' }}>
                <SydInput
                  minWidth={MIN_W}
                  value={form.timeSeriesId}
                  onChange={(e) => setField('timeSeriesId', e.target.value)}
                  placeholder='Enter a code or generate one'
                  disabled={!isNew}
                />
                {isNew && (
                  <SydButton
                    variant='outline'
                    size='sm'
                    onClick={() => setField('timeSeriesId', crypto.randomUUID())}
                  >
                    Generate
                  </SydButton>
                )}
              </FlexRow>
            </Grid>
            <Grid item xs={12} md={6}>
              <label className={classes.label}>Name</label>
              <SydInput
                minWidth={MIN_W}
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder='Data mart name'
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <label className={classes.label}>Description</label>
              <SydInput
                minWidth={MIN_W}
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                placeholder='Description'
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <label className={classes.label}>Enabled</label>
              <SydSwitch
                value={form.enabled}
                onChange={(e, checked) => setField('enabled', checked)}
              />
            </Grid>
          </Grid>
        </div>
      </Grid>

      {/* Type Selector */}
      <Grid item xs={12}>
        <div className={classes.section}>
          <div className={classes.sectionTitle}>Type</div>
          <FlexRow style={{ gap: '8px' }}>
            {WORKFLOW_TYPES.map(wt => (
              <SydButton
                key={wt}
                className={classes.typeButton}
                variant={workflowType === wt ? 'primary' : 'outline'}
                size='sm'
                onClick={() => setWorkflowType(wt)}
              >
                {wt === 'timeseries' ? 'Time Series' : wt.charAt(0).toUpperCase() + wt.slice(1)}
              </SydButton>
            ))}
          </FlexRow>
        </div>
      </Grid>

      {/* Pipeline Selector */}
      <Grid item xs={12}>
        <div className={classes.section}>
          <div className={classes.sectionTitle}>Pipeline</div>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <label className={classes.label}>Source Pipeline</label>
              <SydSelect
                minWidth={MIN_W}
                value={form.configuration.sourcePipelineId}
                onChange={(e) => setConfig('sourcePipelineId', e.target.value)}
                options={[
                  { value: '', label: '-- Select a pipeline --' },
                  ...pipelineList.map(p => ({ value: p.code, label: `${p.code} (${p.displayName || ''})` }))
                ]}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {pipelineDetail && (
                <div style={{ paddingTop: '24px' }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                    Pipeline has {pipelineDetail.analysis?.fields?.length || 0} analysis fields
                  </span>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </Grid>

      {/* Pipeline Variables */}
      {pipelineVariables && (
        <Grid item xs={12}>
          <div className={classes.section}>
            <div className={classes.sectionTitle}>Pipeline Variables</div>

            {pipelineVariables.autoProvided.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <label className={classes.label}>Auto-provided by runtime</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {pipelineVariables.autoProvided.map(([k]) => (
                    <span
                      key={k}
                      style={{
                        padding: '2px 8px',
                        border: 'var(--border-neutral-thin)',
                        borderRadius: 'var(--border-radius-sm)',
                        fontSize: '0.75rem',
                        color: 'var(--color-text-secondary)'
                      }}
                    >
                      {k}
                    </span>
                  ))}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginTop: '4px' }}>
                  These are populated automatically based on run-by, periods, and batch context.
                </div>
              </div>
            )}

            {pipelineVariables.userConfigurable.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <label className={classes.label}>Configurable parameters</label>
                <Grid container spacing={1}>
                  {pipelineVariables.userConfigurable.map(([k, defaultValue]) => (
                    <Grid item xs={12} md={4} key={k}>
                      <label className={classes.label} style={{ fontSize: '0.75rem' }}>{k}</label>
                      <SydInput
                        minWidth={MIN_W}
                        value={defaultParams[k] !== undefined ? String(defaultParams[k]) : (defaultValue !== undefined ? String(defaultValue) : '')}
                        onChange={(e) => handleDefaultParamChange(k, e.target.value)}
                        placeholder={defaultValue !== undefined ? String(defaultValue) : ''}
                        size='sm'
                      />
                    </Grid>
                  ))}
                </Grid>
              </div>
            )}

            {pipelineVariables.dateEntries.length > 0 && (
              <div>
                <label className={classes.label}>Date variables (auto-provided)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {pipelineVariables.dateEntries.map(([k]) => (
                    <span
                      key={k}
                      style={{
                        padding: '2px 8px',
                        border: 'var(--border-neutral-thin)',
                        borderRadius: 'var(--border-radius-sm)',
                        fontSize: '0.75rem',
                        color: 'var(--color-text-secondary)'
                      }}
                    >
                      {k}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {pipelineVariables.autoProvided.length === 0 && pipelineVariables.userConfigurable.length === 0 && pipelineVariables.dateEntries.length === 0 && (
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                No variables defined on this pipeline.
              </div>
            )}
          </div>
        </Grid>
      )}

      {/* Type-Specific Options */}
      {workflowType === 'timeseries' && (
        <Grid item xs={12}>
          <div className={classes.section}>
            <div className={classes.sectionTitle}>Time Series Options</div>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <label className={classes.label}>Periods</label>
                <FlexRow style={{ gap: '8px', flexWrap: 'wrap' }}>
                  {PERIOD_OPTIONS.map(period => {
                    const checked = (form.configuration.periods || []).includes(period)
                    return (
                      <label key={period} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                        <input
                          type='checkbox'
                          checked={checked}
                          onChange={() => {
                            const current = form.configuration.periods || []
                            const next = checked
                              ? current.filter(p => p !== period)
                              : [...current, period]
                            setConfig('periods', next)
                          }}
                        />
                        {period}
                      </label>
                    )
                  })}
                </FlexRow>
              </Grid>
              <Grid item xs={12} md={4}>
                <label className={classes.label}>Min Date</label>
                <SydInput
                  minWidth={MIN_W}
                  type='date'
                  value={form.configuration.minDate || ''}
                  onChange={(e) => setConfig('minDate', e.target.value || null)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <label className={classes.label}>Max Date</label>
                <SydInput
                  minWidth={MIN_W}
                  type='date'
                  value={form.configuration.maxDate || ''}
                  onChange={(e) => setConfig('maxDate', e.target.value || null)}
                />
              </Grid>
            </Grid>
          </div>
        </Grid>
      )}

      {workflowType === 'snapshot' && (
        <Grid item xs={12}>
          <div className={classes.section}>
            <div className={classes.sectionTitle}>Snapshot Options</div>
            <div className={classes.infoNote} style={{ marginBottom: '12px' }}>
              Snapshot type requires a <strong>snapshotDate</strong> column in the dataset as part of the key.
              Data is reset based on the snapshot date column value.
            </div>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <label className={classes.label}>Reset Strategy</label>
                <SydSelect
                  minWidth={MIN_W}
                  value={form.configuration.behaviors?.resetStrategy || 'snapshotDate'}
                  onChange={(e) => setBehavior('resetStrategy', e.target.value)}
                  options={[
                    { value: 'snapshotDate', label: 'Snapshot Date' },
                    { value: 'rowVersion', label: 'Row Version' }
                  ]}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <label className={classes.label}>Snapshot Date Column</label>
                <SydInput
                  minWidth={MIN_W}
                  value={form.configuration.behaviors?.snapshotDateColumn || 'snapshotDate'}
                  onChange={(e) => setBehavior('snapshotDateColumn', e.target.value)}
                  placeholder='snapshotDate'
                />
              </Grid>
            </Grid>
          </div>
        </Grid>
      )}

      {workflowType === 'fact' && (
        <Grid item xs={12}>
          <div className={classes.section}>
            <div className={classes.sectionTitle}>Fact Options</div>
            <div className={classes.infoNote}>
              Fact data marts represent current-state data. The primary key columns determine the
              unique key for each row. On each run, existing rows matching the key are replaced
              with the latest data from the pipeline.
            </div>
          </div>
        </Grid>
      )}

      {/* Run By & Paging */}
      <Grid item xs={12}>
        <div className={classes.section}>
          <div className={classes.sectionTitle}>Execution</div>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <label className={classes.label}>Run By</label>
              <SydSelect
                minWidth={MIN_W}
                value={form.configuration.runBy}
                onChange={(e) => setConfig('runBy', e.target.value)}
                options={RUN_BY_OPTIONS.map(o => ({ value: o, label: o }))}
              />
            </Grid>
            {runBy === 'system-query' && (
              <Grid item xs={12} md={4}>
                <label className={classes.label}>Counting System Query</label>
                <SydSelect
                  minWidth={MIN_W}
                  value={form.configuration.systemQueryCode || ''}
                  onChange={(e) => setConfig('systemQueryCode', e.target.value || null)}
                  options={[
                    { value: '', label: '-- Select a counting query --' },
                    ...systemQueryList.map(sq => ({ value: sq.code, label: `${sq.code} (${sq.name || ''})` }))
                  ]}
                />
              </Grid>
            )}
            {runBy === 'system-query' && (
              <Grid item xs={12}>
                <div className={classes.infoNote}>
                  System query mode uses skip/take pagination instead of entity batching.
                  This requires a <strong>counting query</strong> &mdash; a system query that returns
                  exactly one row with a column named <strong>rowCount</strong> (the total number of
                  records to process). The source pipeline must
                  accept <strong>@filters.skip</strong> and <strong>@filters.take</strong>.
                </div>
              </Grid>
            )}
            {runBy === 'pipeline' && (
              <Grid item xs={12} md={4}>
                <label className={classes.label}>Counting Pipeline</label>
                <SydSelect
                  minWidth={MIN_W}
                  value={form.configuration.countingPipelineCode || ''}
                  onChange={(e) => setConfig('countingPipelineCode', e.target.value || null)}
                  options={[
                    { value: '', label: '-- Select a counting pipeline --' },
                    ...pipelineList.map(p => ({ value: p.code, label: `${p.code} (${p.displayName || ''})` }))
                  ]}
                />
              </Grid>
            )}
            {runBy === 'pipeline' && (
              <Grid item xs={12}>
                <div className={classes.infoNote}>
                  Pipeline mode uses skip/take pagination like system-query, but the counting
                  pipeline receives the full variable context (<strong>asOfDate</strong>,
                  {' '}<strong>defaultParameters</strong>, and batch parameters).
                  The counting pipeline must return a single row with
                  a <strong>rowCount</strong> field, or a plain number.
                  The source pipeline must accept <strong>@filters.skip</strong> and <strong>@filters.take</strong>.
                </div>
              </Grid>
            )}
            {runBy === 'none' && (
              <Grid item xs={12}>
                <div className={classes.infoNote}>
                  No batching -- a single task processes all data.
                </div>
              </Grid>
            )}
            {['client', 'account', 'tag', 'asset'].includes(runBy) && (
              <Grid item xs={12}>
                <label className={classes.label}>Exposure Filter</label>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={7}>
                    <JsonEditor
                      value={form.configuration.exposureFilter}
                      onChange={handleExposureFilterChange}
                      height='150px'
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <div className={classes.infoNote}>
                      {runBy === 'client' && (
                        <div>
                          <div style={{ marginBottom: '4px' }}><strong>Client filter options:</strong></div>
                          <code style={{ fontSize: '0.75rem' }}>clientIds</code> -- ValueFilter on client IDs
                          <div style={{ marginTop: '6px', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                            Example: <code>{'{ "clientIds": { "op": "in", "values": [1, 2, 3] } }'}</code>
                          </div>
                        </div>
                      )}
                      {runBy === 'account' && (
                        <div>
                          <div style={{ marginBottom: '4px' }}><strong>Account filter options:</strong></div>
                          <code style={{ fontSize: '0.75rem' }}>accountIds</code> -- filter by account IDs<br />
                          <code style={{ fontSize: '0.75rem' }}>clientIds</code> -- filter accounts by parent client
                          <div style={{ marginTop: '6px', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                            Example: <code>{'{ "clientIds": { "op": "in", "values": [5] } }'}</code>
                          </div>
                        </div>
                      )}
                      {runBy === 'tag' && (
                        <div>
                          <div style={{ marginBottom: '4px' }}><strong>Tag filter options:</strong></div>
                          <code style={{ fontSize: '0.75rem' }}>tagOptions</code> -- array of tag definitions<br />
                          Each: <code style={{ fontSize: '0.75rem' }}>{'{ tagTypeName, tagType }'}</code><br />
                          tagType: <code style={{ fontSize: '0.75rem' }}>&quot;group&quot;</code> or <code style={{ fontSize: '0.75rem' }}>&quot;classificationTag&quot;</code>
                          <div style={{ marginTop: '6px', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                            Example: <code>{'{ "tagOptions": [{ "tagTypeName": "Strategy", "tagType": "group" }] }'}</code>
                          </div>
                        </div>
                      )}
                      {runBy === 'asset' && (
                        <div>
                          <div style={{ marginBottom: '4px' }}><strong>Asset filter options:</strong></div>
                          <code style={{ fontSize: '0.75rem' }}>holdingStatus</code> -- <code style={{ fontSize: '0.75rem' }}>&quot;everHeld&quot;</code> or <code style={{ fontSize: '0.75rem' }}>&quot;currentlyHeld&quot;</code><br />
                          <code style={{ fontSize: '0.75rem' }}>assetIds</code> -- ValueFilter on asset IDs
                          <div style={{ marginTop: '6px', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                            Example: <code>{'{ "holdingStatus": "currentlyHeld" }'}</code>
                          </div>
                        </div>
                      )}
                      <div style={{ marginTop: '8px', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                        Empty filter <code>{'{}'}</code> processes all {runBy}s.
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid item xs={12} md={2}>
              <label className={classes.label}>Batch Size</label>
              <SydInput
                minWidth={MIN_W}
                type='number'
                value={form.configuration.batchSize}
                onChange={(e) => setConfig('batchSize', parseInt(e.target.value, 10) || 0)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <label className={classes.label}>Task Page Size</label>
              <SydInput
                minWidth={MIN_W}
                type='number'
                value={form.configuration.taskPageSize}
                onChange={(e) => setConfig('taskPageSize', parseInt(e.target.value, 10) || 0)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <label className={classes.label}>Data Accessor</label>
              <SydInput
                minWidth={MIN_W}
                value={form.configuration.dataAccessor}
                onChange={(e) => setConfig('dataAccessor', e.target.value)}
                placeholder='data'
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <label className={classes.label}>Process Tasks Sequentially</label>
              <SydSelect
                minWidth={MIN_W}
                value={form.configuration.processTasksSequentially ? 'true' : 'false'}
                onChange={(e) => setConfig('processTasksSequentially', e.target.value === 'true')}
                options={[
                  { value: 'false', label: 'No (parallel)' },
                  { value: 'true', label: 'Yes (sequential)' }
                ]}
              />
            </Grid>
          </Grid>
        </div>
      </Grid>

      {/* Table Configuration */}
      <Grid item xs={12}>
        <div className={classes.section}>
          <div className={classes.sectionTitle}>Table Configuration</div>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <label className={classes.label}>Table Name</label>
              <SydInput
                minWidth={MIN_W}
                value={form.configuration.tableConfiguration.tableName}
                onChange={(e) => setTableConfig('tableName', e.target.value)}
                placeholder='tableName'
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <label className={classes.label}>Table Schema</label>
              <SydInput
                minWidth={MIN_W}
                value='dataExport'
                disabled
              />
            </Grid>
          </Grid>
        </div>
      </Grid>

      {/* Columns */}
      <Grid item xs={12}>
        <div className={classes.section}>
          <FlexRow style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div className={classes.sectionTitle} style={{ marginBottom: 0 }}>Columns</div>
            <SydButton
              variant='outline'
              size='sm'
              onClick={deriveColumnsFromPipeline}
              disabled={!pipelineDetail?.analysis?.fields?.length}
            >
              Reset from Pipeline
            </SydButton>
          </FlexRow>
          <ColumnsEditor
            columns={form.configuration.tableConfiguration.columns}
            onChange={(cols) => setTableConfig('columns', cols)}
            dataTypes={DATA_TYPES}
          />
        </div>
      </Grid>

      {/* Indexes */}
      <Grid item xs={12}>
        <div className={classes.section}>
          <div className={classes.sectionTitle}>Indexes</div>
          <IndexesEditor
            indexes={form.configuration.tableConfiguration.indexes}
            columns={form.configuration.tableConfiguration.columns}
            onChange={(idxs) => setTableConfig('indexes', idxs)}
          />
        </div>
      </Grid>

      {/* Exclude from Insert */}
      <Grid item xs={12}>
        <div className={classes.section}>
          <div className={classes.sectionTitle}>Exclude from Insert</div>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <label className={classes.label}>Columns to exclude</label>
              <SydInput
                minWidth={MIN_W}
                value={(form.configuration.excludeFromInsert || []).join(', ')}
                onChange={(e) => {
                  const val = e.target.value
                  setConfig('excludeFromInsert', val ? val.split(',').map(s => s.trim()).filter(Boolean) : [])
                }}
                placeholder='Comma-separated column names'
              />
            </Grid>
          </Grid>
        </div>
      </Grid>

    </Grid>
  )
}

ConfigurationTab.propTypes = {
  form: PropTypes.object.isRequired,
  isNew: PropTypes.bool,
  isSaving: PropTypes.bool,
  pipelineList: PropTypes.array,
  pipelineDetail: PropTypes.object,
  setField: PropTypes.func.isRequired,
  setConfig: PropTypes.func.isRequired,
  setTableConfig: PropTypes.func.isRequired,
  setBehavior: PropTypes.func.isRequired,
  setWorkflowType: PropTypes.func.isRequired,
  deriveColumnsFromPipeline: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  WORKFLOW_TYPES: PropTypes.array.isRequired,
  RUN_BY_OPTIONS: PropTypes.array.isRequired,
  PERIOD_OPTIONS: PropTypes.array.isRequired,
  DATA_TYPES: PropTypes.array.isRequired
}

export default ConfigurationTab
